"use client";

/**
 * The three-step rail above the Apply Now form — ported from the source's
 * `FormProgressIndicator`.
 *
 * Steps 1 and 2 are green once reached. Step 3 is the exception: it stays
 * outlined until a payment outcome exists, then takes its colour from that
 * outcome (green / red / amber) rather than from the step number.
 */

interface FormProgressIndicatorProps {
  currentStep: number;
  /** "SUCCESS" | "FAILED" | "PENDING", once the payment has been looked up. */
  paymentStatus?: string;
}

const GREEN = "bg-[#4AD295] text-white";
const OUTLINED = "border border-[#737373] bg-transparent text-gray-500";
const CIRCLE = "rounded-full w-12 h-12 flex items-center justify-center";
const CAPTION = "text-xs tab:text-sm font-medium mt-1 text-center";

/** lucide CheckCircle2, inlined. */
function CheckIcon() {
  return (
    <svg
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

/** lucide CircleAlert, inlined. */
function AlertIcon() {
  return (
    <svg
      className="h-6 w-6 text-[#ffffff]"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

export default function FormProgressIndicator({
  currentStep,
  paymentStatus,
}: FormProgressIndicatorProps) {
  // Step 3's circle: outcome first, step position only as a fallback.
  const paymentCircle =
    currentStep > 3
      ? GREEN
      : currentStep === 3 && paymentStatus === "SUCCESS"
        ? GREEN
        : currentStep === 3 && paymentStatus === "FAILED"
          ? "bg-[#E03539]"
          : currentStep === 3 && paymentStatus === "PENDING"
            ? "bg-[#F0A727]"
            : OUTLINED;

  return (
    <div className="flex items-center justify-between mb-8 relative">
      {/* The rail the circles sit on. */}
      <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 z-0" />

      <div className="flex flex-col items-center z-10 bg-[#FFFFFF] px-2">
        <div className={`${CIRCLE} ${currentStep >= 1 ? GREEN : OUTLINED}`}>
          {currentStep > 1 ? (
            <CheckIcon />
          ) : (
            <span className="text-lg font-medium">01</span>
          )}
        </div>
        <span className={`${CAPTION} text-[#312E2E]`}>Student Information</span>
      </div>

      <div className="flex flex-col items-center z-10 bg-[#FFFFFF] px-2">
        <div className={`${CIRCLE} ${currentStep >= 2 ? GREEN : OUTLINED}`}>
          {currentStep > 2 ? (
            <CheckIcon />
          ) : (
            <span className="text-lg font-medium">02</span>
          )}
        </div>
        <span
          className={`${CAPTION} ${
            currentStep > 1 ? "text-[#312E2E]" : "text-[#737373]"
          }`}
        >
          Choose Courses
        </span>
      </div>

      <div className="flex flex-col items-center z-10 bg-[#FFFFFF] px-2">
        <div className={`${CIRCLE} ${paymentCircle}`}>
          {paymentStatus === "SUCCESS" ? (
            <CheckIcon />
          ) : paymentStatus === "FAILED" || paymentStatus === "PENDING" ? (
            <AlertIcon />
          ) : (
            <span className="text-lg font-medium">03</span>
          )}
        </div>
        <span
          className={`${CAPTION} ${
            currentStep === 3 ? "text-[#312E2E]" : "text-[#737373]"
          }`}
        >
          Payment Information
        </span>
      </div>
    </div>
  );
}
